import React, { useEffect } from 'react';
import { Badge, Button } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';

import Avatar from '../../components/Avatar';
import JsonDisplay from '../../components/JsonDisplay';
import { markErrorLogAsRead } from '../../slices/error-logs.slice';

import './ErrorLogs.scss';

const ERROR_TYPE_CONFIG = {
    server: { color: 'danger', icon: 'fa-server', label: 'Server Error' },
    toast: { color: 'warning', icon: 'fa-comment-exclamation', label: 'Toast Error' },
    exception: { color: 'purple', icon: 'fa-bug', label: 'Exception' }
};

const MetadataCard = ({ icon, label, children }) => (
    <div className="metadata-card">
        <div className="text-white-50 small mb-1">
            <span className={`fas ${icon} me-1`} />
            {label}
        </div>
        <div>{children}</div>
    </div>
);

const ErrorLogDetailPanel = ({ logId, onClose }) => {
    const dispatch = useDispatch();
    const { selectedLog: errorLog, selectedStatus } = useSelector(state => state.errorLogs);

    useEffect(() => {
        if (errorLog && !errorLog.isRead) {
            dispatch(markErrorLogAsRead(logId));
        }
    }, [dispatch, logId, errorLog]);

    if (!errorLog && selectedStatus !== 'loading') {
        return null;
    }

    if (selectedStatus === 'loading') {
        return (
            <div className="error-log-panel bg-broken shadow-lg">
                <div className="error-log-panel-header">
                    <h5 className="mb-0">Loading...</h5>
                    <button className="btn btn-link text-white p-0" onClick={onClose}>
                        <span className="fas fa-times fa-lg" />
                    </button>
                </div>
                <div className="error-log-panel-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    const {
        errorType,
        message,
        stack,
        frontendUrl,
        userAgent,
        requestUrl,
        requestMethod,
        requestPayload,
        responseStatus,
        responseData,
        user,
        occurrenceCount,
        lastOccurredAt,
        createdAt
    } = errorLog;

    const typeConfig = ERROR_TYPE_CONFIG[errorType] || ERROR_TYPE_CONFIG.exception;

    return (
        <div className="error-log-panel bg-broken shadow-lg">
            {/* Header */}
            <div className="error-log-panel-header">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge
                        color={typeConfig.color}
                        className={typeConfig.color === 'purple' ? 'bg-purple' : ''}
                        style={{ borderRadius: 0 }}
                    >
                        <span className={`fas ${typeConfig.icon} me-1`} />
                        {typeConfig.label}
                    </Badge>
                    {responseStatus && (
                        <Badge
                            color={responseStatus >= 500 ? 'danger' : 'warning'}
                            style={{ borderRadius: 0 }}
                        >
                            HTTP {responseStatus}
                        </Badge>
                    )}
                    {occurrenceCount > 1 && (
                        <Badge color="secondary" style={{ borderRadius: 0 }}>
                            <span className="fas fa-layer-group me-1" />
                            {occurrenceCount} occurrences
                        </Badge>
                    )}
                </div>
                <button className="btn btn-link text-white p-0" onClick={onClose}>
                    <span className="fas fa-times fa-lg" />
                </button>
            </div>

            {/* Body */}
            <div className="error-log-panel-body">
                {/* Message Banner */}
                <div className="details-banner mb-4">
                    <p className="mb-0">{message}</p>
                </div>

                {/* Metadata Grid */}
                <div className="metadata-grid mb-4">
                    <MetadataCard icon="fa-calendar" label="First Occurred">
                        {new Date(createdAt).toLocaleString()}
                    </MetadataCard>

                    {occurrenceCount > 1 && (
                        <MetadataCard icon="fa-clock" label="Last Occurred">
                            {new Date(lastOccurredAt).toLocaleString()}
                        </MetadataCard>
                    )}

                    {frontendUrl && (
                        <MetadataCard icon="fa-map-marker-alt" label="Frontend URL">
                            <code>{frontendUrl}</code>
                        </MetadataCard>
                    )}

                    {userAgent && (
                        <MetadataCard icon="fa-globe" label="User Agent">
                            <span className="small text-truncate d-block" title={userAgent}>
                                {userAgent}
                            </span>
                        </MetadataCard>
                    )}
                </div>

                {/* User Info */}
                {user && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">User</div>
                        <div className="d-flex align-items-center gap-3 p-2 bg-black/30 rounded">
                            <Avatar
                                imageUrl={user.profilePic?.imageUrl}
                                name={`${user.firstName} ${user.lastName}`}
                                size={40}
                            />
                            <div>
                                <div className="fw-bold">
                                    {user.firstName} {user.lastName}
                                </div>
                                <code className="small text-white-50">@{user.username}</code>
                            </div>
                        </div>
                    </div>
                )}

                {/* Server Error Details */}
                {errorType === 'server' && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-server me-1" />
                            Request Details
                        </div>
                        <div className="p-3 bg-black/30 rounded border-start border-danger border-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                {requestMethod && (
                                    <Badge color="info" style={{ borderRadius: 0 }}>
                                        {requestMethod}
                                    </Badge>
                                )}
                                {requestUrl && (
                                    <code className="small text-white-50">{requestUrl}</code>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Request Payload */}
                {requestPayload && Object.keys(requestPayload).length > 0 && (
                    <div className="mb-4">
                        <JsonDisplay
                            data={requestPayload}
                            title="Request Payload"
                            defaultOpen={false}
                        />
                    </div>
                )}

                {/* Response Data */}
                {responseData && Object.keys(responseData).length > 0 && (
                    <div className="mb-4">
                        <JsonDisplay
                            data={responseData}
                            title="Response Data"
                            defaultOpen={true}
                        />
                    </div>
                )}

                {/* Stack Trace */}
                {stack && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-layer-group me-1" />
                            Stack Trace
                        </div>
                        <div className="p-3 bg-black rounded border-start border-purple border-3" style={{ maxHeight: '400px', overflow: 'auto' }}>
                            <pre className="mb-0 small text-white-50" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {stack}
                            </pre>
                        </div>
                    </div>
                )}

                {/* Mark as Read Button */}
                {!errorLog.isRead && (
                    <div className="text-center mt-4">
                        <Button color="primary" outline onClick={() => dispatch(markErrorLogAsRead(logId))}>
                            <span className="fas fa-check me-2" />
                            Mark as Read
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ErrorLogDetailPanel;
